// react
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @mui
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
// components
import Iconify from '../../../../components/Iconify';
// actions
import * as actionsMemberships from '../../../../redux/shop/memberships/actions';
// sections
import MembershipDetail from './MembershipDetail';

/**
 * Inline checkout for a specific perk.
 *
 * Renders the full MembershipDetail UI (plan selector, payment method,
 * coupon, Stripe checkout) inline — so tool pages don't redirect users to
 * /store/memberships just to buy the product that tool needs.
 *
 * Props:
 *   perk      — the USER_PERK_NAME_CHOICES code (e.g. "TR_SUB", "TR", "BP").
 *   onClose   — optional; shows a "Volver" button above the checkout.
 */
export default function InlinePerkUpsell({ perk, onClose }) {
    const dispatch = useDispatch();
    const { data, loadingMemberships, initialMemberships, errorMemberships } =
        useSelector((state) => state.shop.memberships);

    useEffect(() => {
        if (!initialMemberships && !loadingMemberships) {
            dispatch(actionsMemberships.fetchMembershipsRequest());
        }
    }, [initialMemberships, loadingMemberships, dispatch]);

    const product = useMemo(
        () => (data || []).find((p) => p.perk === perk),
        [data, perk],
    );

    if (loadingMemberships || !initialMemberships) {
        return (
            <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (errorMemberships) {
        return (
            <Alert severity="error" sx={{ my: 3 }}>
                No pudimos cargar la opción de pago. Recarga la página o intenta de nuevo.
            </Alert>
        );
    }

    if (!product) {
        return (
            <Alert severity="warning" sx={{ my: 3 }}>
                Este producto no está disponible por ahora.
            </Alert>
        );
    }

    return (
        <Stack spacing={3}>
            {onClose && (
                <Stack direction="row">
                    <Button
                        variant="text"
                        onClick={onClose}
                        startIcon={<Iconify icon="eva:arrow-back-outline" />}
                    >
                        Volver
                    </Button>
                </Stack>
            )}
            <MembershipDetail data={product} />
        </Stack>
    );
}
